import { Component, ChangeDetectionStrategy, inject, OnInit, OnDestroy, effect, untracked, output, signal, computed, viewChild, AfterViewInit, DestroyRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NgTemplateOutlet } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, FormsModule, Validators } from '@angular/forms';
import { Subject, of, debounceTime, distinctUntilChanged, switchMap, catchError } from 'rxjs';
import { InputText } from 'primeng/inputtext';
import { Card } from 'primeng/card';
import { EditorModule, Editor } from 'primeng/editor';
import { ButtonModule } from 'primeng/button';
import { MessageModule } from 'primeng/message';
import { SelectModule } from 'primeng/select';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { CheckboxModule } from 'primeng/checkbox';
import { AutoCompleteModule, AutoCompleteCompleteEvent, AutoCompleteSelectEvent } from 'primeng/autocomplete';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import {
  MapPickerComponent,
  MapPickerConfig,
  AddressFieldsConfig,
  Location,
  Coordinates,
  GeocodingService,
  AutocompleteSuggestion,
  GeocodingConfig,
  BoundingBox
} from '@konradkrk/ngx-map-picker';
import Quill from 'quill';
import { registerQuillEmoji } from '@app/shared/configs/quill-emoji.config';
import { PropertyWizardStateService, WizardValidationService } from '../../services';
import { GalleryUploadComponent, GalleryUploadState } from '../shared/gallery-upload/gallery-upload.component';
import { AmenitySelectorComponent } from '../shared/amenity-selector/amenity-selector.component';
import { WizardStepHintComponent } from '../wizard-step-hint/wizard-step-hint.component';
import { PropertyBasicInfo, ImageFile, GeoLocation, PREFERRED_CONTACT_OPTIONS, DEFAULT_CONTACT_VISIBILITY } from '../../models';
import { AmenityApiService } from '../../api/services';
import { SettingsService } from '@app/modules/settings/services/settings.service';
import { WIZARD_LIMITS } from '../../config/wizard-limits';
import { htmlRequired, htmlMinLength, htmlMaxLength } from '../../utils/form-validators';

@Component({
  selector: 'app-step-basic-info',
  standalone: true,
  imports: [
    NgTemplateOutlet,
    ReactiveFormsModule,
    FormsModule,
    InputText,
    Card,
    ButtonModule,
    EditorModule,
    SelectModule,
    ToggleSwitchModule,
    CheckboxModule,
    AutoCompleteModule,
    MapPickerComponent,
    MessageModule,
    GalleryUploadComponent,
    AmenitySelectorComponent,
    WizardStepHintComponent,
    TranslocoModule
  ],
  templateUrl: './step-basic-info.component.html',
  styleUrl: './step-basic-info.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class StepBasicInfoComponent implements OnInit, OnDestroy, AfterViewInit {
  private readonly fb = inject(FormBuilder);
  private readonly stateService = inject(PropertyWizardStateService);
  private readonly validationService = inject(WizardValidationService);
  private readonly amenityApiService = inject(AmenityApiService);
  private readonly settingsService = inject(SettingsService);
  private readonly translocoService = inject(TranslocoService);
  private readonly geocoding = inject(GeocodingService);
  private readonly destroyRef = inject(DestroyRef);

  // UWAGA: nazwa NIE może brzmieć `cancel` — natywne zdarzenie DOM `cancel`
  // z <input type="file"> (galeria) bąbelkuje do hosta i odpalałoby ten output
  // przy anulowaniu systemowego okna wyboru pliku (wyrzucało z kreatora).
  readonly cancelled = output<void>();

  readonly limits = WIZARD_LIMITS;
  private readonly PHONE_PATTERN = /^\+?[0-9\s\-()]{6,20}$/;
  readonly totalImages = this.stateService.totalImages;

  /** Flaga — użytkownik próbował przejść dalej */
  readonly submitted = signal(false);

  /** Lista brakujących pól do wyświetlenia nad przyciskiem "Dalej" */
  readonly missingFields = signal<string[]>([]);

  /** Stan uploadu z galerii — blokuje "Dalej" dopóki zdjęcia się wgrywają lub mają błąd */
  private readonly galleryUploadsSettled = signal(true);
  private readonly galleryPendingCount = signal(0);
  private readonly galleryFailedCount = signal(0);

  /** ViewChild do edytora opisu */
  private readonly descriptionEditor = viewChild<Editor>('descriptionEditor');

  readonly editorModules = {
    'emoji-toolbar': true,
    clipboard: {
      matchVisual: false,
      matchers: [
        ['img', () => {
          const Delta = Quill.import('delta') as any;
          return new Delta();
        }],
        ['a', (_node: HTMLElement, delta: any) => {
          const Delta = Quill.import('delta') as any;
          return delta.compose(new Delta().retain(delta.length(), { link: false }));
        }],
        [Node.ELEMENT_NODE, (node: HTMLElement, delta: any) => {
          if (['DIV', 'P', 'SECTION', 'ARTICLE'].includes(node.tagName)) {
            const ops = delta.ops;
            if (ops.length > 0) {
              const lastOp = ops[ops.length - 1];
              if (typeof lastOp?.insert === 'string' && !lastOp.insert.endsWith('\n')) {
                const Delta = Quill.import('delta') as any;
                return delta.compose(new Delta().retain(delta.length()).insert('\n'));
              }
            }
          }
          return delta;
        }]
      ]
    }
  };

  constructor() {
    registerQuillEmoji();

    // Autocomplete: miejscowość
    this.geocoding
      .autocompleteStream(this.citySearch$.asObservable(), this.geocodingConfig)
      .pipe(takeUntilDestroyed())
      .subscribe(suggestions => this.citySuggestions.set(suggestions));

    // Autocomplete: ulica (zapytanie doklei miasto w onStreetSearch).
    // Wyszukiwanie ograniczamy do granic wybranego miasta (viewBox + bounded),
    // żeby Nominatim nie podpowiadał ulic z sąsiednich miejscowości tej samej
    // gminy (np. "Lśniących Wód" w Dębinie przy wyborze miasta Ustka).
    // Box czytamy per-zapytanie, bo autocompleteStream zamraża config raz.
    this.streetSearch$
      .pipe(
        debounceTime(500),
        distinctUntilChanged(),
        switchMap(query =>
          this.geocoding.autocomplete(query, {
            ...this.geocodingConfig,
            viewBox: this.selectedCityBounds ?? undefined,
            bounded: !!this.selectedCityBounds
          }).pipe(catchError(() => of([])))
        ),
        takeUntilDestroyed()
      )
      .subscribe(suggestions => this.streetSuggestions.set(suggestions));
  }

  readonly preferredContactOptions = computed(() =>
    PREFERRED_CONTACT_OPTIONS.map(opt => ({
      ...opt,
      label: this.translocoService.translate(opt.labelKey)
    }))
  );

  /** Udogodnienia pobierane z API (type = 'property') */
  readonly propertyAmenities = this.amenityApiService.propertyAmenities;
  readonly amenitiesLoading = this.amenityApiService.loading;
  readonly isViewMode = this.stateService.isViewMode;
  readonly canGoNext = this.stateService.canGoNext;

  form!: FormGroup;

  readonly mapConfig: Partial<MapPickerConfig> = {
    mode: 'map-only',
    styling: {
      height: '300px',
      borderRadius: '8px',
    },
    features: {
      addressSearch: false,
      reverseGeocoding: true,
      draggableMarker: true,
      zoomControls: true,
      clickToSelect: true,
      scrollWheelZoom: false,
    },
    marker: {
      opacity: 1,
    },
  };

  readonly addressFieldsConfig: AddressFieldsConfig = {
    fieldMappings: {
      houseNumber: 'streetNumber',
    },
    syncFields: ['city', 'street', 'houseNumber', 'postalCode', 'country'],
    clearOnEmpty: false,
  };

  readonly geocodingConfig: GeocodingConfig = {
    language: 'pl',
    countryCodes: ['pl'],
    maxSuggestions: 8
  };

  /** Polish labels for OSM place types shown as badge in the suggestion list */
  private readonly placeLabels: Record<string, string> = {
    village: 'wieś',
    hamlet: 'przysiółek',
    isolated_dwelling: 'osada',
    town: 'miasto',
    city: 'miasto',
    municipality: 'gmina',
    administrative: 'jedn. adm.',
    suburb: 'dzielnica',
    neighbourhood: 'osiedle',
    quarter: 'kwartał',
    road: 'ulica',
    pedestrian: 'ulica',
    residential: 'ulica',
    living_street: 'ulica',
    house: 'budynek'
  };

  private readonly citySearch$ = new Subject<string>();
  private readonly streetSearch$ = new Subject<string>();
  readonly citySuggestions = signal<AutocompleteSuggestion[]>([]);
  readonly streetSuggestions = signal<AutocompleteSuggestion[]>([]);

  /**
   * Stan "tylko do odczytu" pól ulicy i numeru (patrz syncAddressFieldState()).
   * Świadomie NIE używamy FormControl.disable() — wyłączony p-autoComplete/input
   * renderuje atrybut `disabled` i po cichu połyka każdy klawisz, przez co pola
   * sprawiały wrażenie zablokowanych po pierwszym wpisaniu.
   */
  readonly streetReadonly = signal(true);
  readonly streetNumberReadonly = signal(true);

  /** Granice (bounding box) wybranego miasta — ograniczają wyszukiwanie ulic do tego boxa. */
  private selectedCityBounds: BoundingBox | null = null;

  /**
   * Miejscowość, dla której pobrano `selectedCityBounds` (z onCitySelect).
   * Box obowiązuje wyłącznie dla tej miejscowości — gdy `city` zmieni się na inną
   * (ręczny wpis albo podstawienie z mapy), poprzedni box jest nieaktualny i musi
   * zostać wyczyszczony, inaczej ukrywałby poprawne ulice nowego miasta.
   */
  private boundsCity: string | null = null;

  /** Ostatnie zapytanie wysłane do geocodera — chroni przed dublami po (blur). */
  private lastGeocodedQuery = '';

  readonly addressFormGroup = computed(() => this.form?.get('address') as FormGroup);

  /** ViewChild do mapy */
  private readonly mapPicker = viewChild<MapPickerComponent>('mapPicker');

  /** Flaga czy mapa jest gotowa */
  private readonly _mapReady = signal(false);

  /** Współrzędne do ustawienia na mapie po jej załadowaniu */
  private readonly _pendingCoordinates = signal<Coordinates | null>(null);

  private readonly stateEffect = effect(() => {
    const basicInfo = this.stateService.basicInfo();
    if (basicInfo && this.form) {
      // Hydratacja formularza ze stanu to efekt uboczny — NIE może tworzyć
      // zależności reaktywnych. patchFormFromState() czyta sygnał _mapReady
      // (gałąź z location, aktywna tylko w trybie edycji), więc bez untracked()
      // późniejsze załadowanie mapy (onMapReady → _mapReady=true) ponownie
      // odpalało ten efekt ze STARYM basicInfo i nadpisywało miejscowość/adres
      // wpisywany właśnie przez użytkownika. untracked() zawęża zależność efektu
      // wyłącznie do basicInfo().
      untracked(() => this.patchFormFromState(basicInfo));
    }
  });

  ngAfterViewInit(): void {
    // Block image drag-and-drop into the editor
    const editor = this.descriptionEditor();
    if (editor) {
      const editorEl = (editor as any).el?.nativeElement?.querySelector('.ql-editor');
      if (editorEl) {
        editorEl.addEventListener('drop', (e: DragEvent) => {
          if (e.dataTransfer?.files?.length) {
            e.preventDefault();
            e.stopPropagation();
          }
        });
      }
    }
  }

  ngOnInit(): void {
    this.initForm();
    this.setupFormSubscription();
    this.setupAddressFieldEnableLogic();

    // Załaduj udogodnienia z API
    this.amenityApiService.loadAmenities().subscribe();

    const existingData = this.stateService.basicInfo();
    if (existingData) {
      this.patchFormFromState(existingData);
    }

    // W trybie tworzenia: pobierz dane kontaktowe z ustawień i podstaw jako domyślne
    if (this.stateService.mode() === 'create') {
      this.prefillContactFromSettings();
    }
  }

  ngOnDestroy(): void {
    this.saveToState();
  }

  private initForm(): void {
    this.form = this.fb.group({
      name: ['', [Validators.required, Validators.minLength(3), Validators.maxLength(100)]],
      registrationNumber: ['', [Validators.maxLength(50)]],
      description: ['', [htmlRequired(), htmlMinLength(50), htmlMaxLength(5000)]],
      address: this.fb.group({
        city: ['', Validators.required],
        street: [''],         // opcjonalnie — wsie mogą nie mieć ulicy
        streetNumber: [''],   // opcjonalnie — nie zawsze jest numer
        postalCode: ['', [Validators.required, Validators.pattern(/^\d{2}-\d{3}$/)]],
        country: ['Polska', Validators.required],
        noStreet: [false]     // UI: "Brak ulicy" — wyłącza pola ulicy/numeru
      }),
      images: [[], Validators.required],
      amenityIds: [[]],
      location: [null],
      phone: ['', [Validators.pattern(this.PHONE_PATTERN)]],
      email: ['', [Validators.email, Validators.maxLength(255)]],
      whatsApp: ['', [Validators.pattern(this.PHONE_PATTERN)]],
      showPhone: [false],
      showWhatsApp: [false],
      showEmail: [false],
      preferredContact: ['form']
    });
  }

  private setupFormSubscription(): void {
    this.form.valueChanges.subscribe(() => {
      this.updateValidity();
    });
  }

  private updateValidity(): void {
    const data = this.getFormData();
    const result = this.validationService.validateBasicInfo(data);
    // Krok jest poprawny tylko gdy wszystkie zdjęcia są wgrane (nic w toku ani błędne).
    this.stateService.setStepValidity(0, result.isValid && this.galleryUploadsSettled());
  }

  /**
   * Reaguje na zmianę stanu uploadu w galerii. Dopóki coś się wgrywa lub
   * któryś plik ma błąd, krok pozostaje niepoprawny — użytkownik nie przejdzie
   * dalej i nie zapisze obiektu z brakującymi/nieudanymi zdjęciami.
   */
  onGalleryUploadState(state: GalleryUploadState): void {
    this.galleryUploadsSettled.set(state.settled);
    this.galleryPendingCount.set(state.pending);
    this.galleryFailedCount.set(state.failed);
    this.updateValidity();
    // imagesChange leci PRZED uploadStateChange, więc saveToState() w onImagesChange
    // wykonuje się jeszcze przy settled=false i jest pomijany. Gdy upload się
    // ustabilizuje, utrwalamy pełną listę zdjęć tutaj.
    if (state.settled) {
      this.saveToState();
    }
    if (this.submitted()) {
      this.updateMissingFields();
    }
  }

  private patchFormFromState(info: PropertyBasicInfo): void {
    const cv = info.contactVisibility || DEFAULT_CONTACT_VISIBILITY;
    this.form.patchValue({
      name: info.name,
      registrationNumber: info.registrationNumber || '',
      description: info.description,
      address: info.address,
      images: info.images,
      amenityIds: info.amenityIds,
      location: info.location || null,
      phone: info.phone || '',
      email: info.email || '',
      whatsApp: info.whatsApp || '',
      showPhone: cv.showPhone,
      showWhatsApp: cv.showWhatsApp,
      showEmail: cv.showEmail,
      preferredContact: cv.preferredContact
    }, { emitEvent: false });

    this.updateContactValidators();

    // Patch leci z { emitEvent: false }, więc subskrypcje valueChanges się nie
    // odpalają — ręcznie przeliczamy stan readonly pól ulicy i numeru.
    this.syncAddressFieldState();

    // Aktualizuj współrzędne dla mapy
    if (info.location?.latitude != null && info.location?.longitude != null) {
      // Upewnij się że współrzędne są liczbami (backend może zwrócić stringi)
      const lat = typeof info.location.latitude === 'string'
        ? parseFloat(info.location.latitude)
        : info.location.latitude;
      const lng = typeof info.location.longitude === 'string'
        ? parseFloat(info.location.longitude)
        : info.location.longitude;

      if (!isNaN(lat) && !isNaN(lng)) {
        const coords: Coordinates = { lat, lng };
        this._pendingCoordinates.set(coords);

        // Jeśli mapa jest już gotowa, ustaw marker od razu
        if (this._mapReady()) {
          this.setMapMarker(coords);
        }
      } else {
        this._pendingCoordinates.set(null);
      }
    } else {
      this._pendingCoordinates.set(null);
    }

    this.updateValidity();
  }

  private saveToState(): void {
    // Nie utrwalaj stanu, dopóki upload galerii nie jest ustabilizowany. Spóźniony
    // callback mapy/geokodowania (onLocationChange / applyLocationFromSuggestion /
    // fillPostalCodeFromCoords) wywołuje saveToState() — gdyby przy pliku jeszcze
    // wgrywanym zapisał niepełną listę zdjęć, efekt hydratacji (stateEffect) nadpisałby
    // formularz tym niepełnym zbiorem i świeżo wgrane zdjęcie nie trafiłoby do payloadu
    // zapisu → backend nigdy by go nie zaclaimował → plik kasowany jako sierota.
    // Po ustabilizowaniu uploadu stan jest dosypywany w onGalleryUploadState().
    if (this.form.dirty && this.galleryUploadsSettled()) {
      const data = this.getFormData();
      if (data) {
        this.stateService.updateBasicInfo(data);
      }
    }
  }

  private getFormData(): PropertyBasicInfo | null {
    const value = this.form.getRawValue();

    if (!value.images || value.images.length === 0) {
      return null;
    }

    // noStreet to flaga UI — nie wysyłamy jej do backendu
    const { noStreet: _omit, ...addressPayload } = value.address ?? {};

    const data: PropertyBasicInfo = {
      name: value.name?.trim() || '',
      registrationNumber: value.registrationNumber?.trim() || '',
      description: value.description?.trim() || '',
      address: addressPayload,
      images: value.images,
      amenityIds: value.amenityIds || [],
      phone: value.phone?.trim() || '',
      email: value.email?.trim() || '',
      whatsApp: value.whatsApp?.trim() || '',
      contactVisibility: {
        showPhone: value.showPhone ?? false,
        showWhatsApp: value.showWhatsApp ?? false,
        showEmail: value.showEmail ?? false,
        preferredContact: value.preferredContact || 'form'
      }
    };

    if (value.location) {
      data.location = value.location;
    }

    return data;
  }

  private prefillContactFromSettings(): void {
    const applyContact = () => {
      const contact = this.settingsService.contactInfo();
      if (!contact) return;

      const patch: Record<string, string> = {};
      if (contact.phone && !this.form.get('phone')?.value) {
        patch['phone'] = contact.phone;
      }
      if (contact.whatsApp && !this.form.get('whatsApp')?.value) {
        patch['whatsApp'] = contact.whatsApp;
      }
      if (contact.email && !this.form.get('email')?.value) {
        patch['email'] = contact.email;
      }

      if (Object.keys(patch).length > 0) {
        this.form.patchValue(patch, { emitEvent: false });
      }
    };

    if (this.settingsService.isLoaded()) {
      applyContact();
    } else {
      this.settingsService.loadSettings().subscribe(() => applyContact());
    }
  }

  onImagesChange(images: ImageFile[]): void {
    this.form.patchValue({ images });
    this.form.markAsDirty();
    this.saveToState();
  }

  onAmenitiesChange(amenityIds: number[]): void {
    this.form.patchValue({ amenityIds });
    this.form.markAsDirty();
    this.saveToState();
  }

  onContactToggleChange(field: string, checked: boolean): void {
    this.form.patchValue({ [field]: checked });
    this.form.markAsDirty();
    this.updateContactValidators();
  }

  private updateContactValidators(): void {
    const showPhone = this.form.get('showPhone')?.value;
    const showWhatsApp = this.form.get('showWhatsApp')?.value;
    const showEmail = this.form.get('showEmail')?.value;

    const phoneControl = this.form.get('phone');
    const whatsAppControl = this.form.get('whatsApp');
    const emailControl = this.form.get('email');

    if (phoneControl) {
      phoneControl.setValidators(showPhone
        ? [Validators.required, Validators.pattern(this.PHONE_PATTERN)]
        : [Validators.pattern(this.PHONE_PATTERN)]);
      phoneControl.updateValueAndValidity({ emitEvent: false });
    }

    if (whatsAppControl) {
      whatsAppControl.setValidators(showWhatsApp
        ? [Validators.required, Validators.pattern(this.PHONE_PATTERN)]
        : [Validators.pattern(this.PHONE_PATTERN)]);
      whatsAppControl.updateValueAndValidity({ emitEvent: false });
    }

    if (emailControl) {
      emailControl.setValidators(showEmail
        ? [Validators.required, Validators.email, Validators.maxLength(255)]
        : [Validators.email, Validators.maxLength(255)]);
      emailControl.updateValueAndValidity({ emitEvent: false });
    }
  }

  onPreferredContactChange(value: string): void {
    this.form.patchValue({ preferredContact: value });
    this.form.markAsDirty();
  }

  onMapReady(): void {
    this._mapReady.set(true);

    // Jeśli są pending coordinates, ustaw marker
    const coords = this._pendingCoordinates();
    if (coords) {
      this.setMapMarker(coords);
    }
  }

  private setMapMarker(coords: Coordinates): void {
    const picker = this.mapPicker();
    if (picker) {
      // Użyj wewnętrznego mapContainer do ustawienia markera
      const mapContainer = picker.mapContainer();
      if (mapContainer) {
        mapContainer.setMarker(coords);
        mapContainer.panTo(coords, 15);
      }
    }
  }

  onLocationChange(location: Location): void {
    // Zapisz współrzędne geograficzne (ukryte dla użytkownika)
    if (location.coordinates) {
      const geoLocation: GeoLocation = {
        latitude: location.coordinates.lat,
        longitude: location.coordinates.lng
      };
      this.form.patchValue({ location: geoLocation });
    }

    if (location.address) {
      const a = location.address;
      const place = a.hamlet ?? a.village ?? a.town ?? a.city ?? '';
      const addressGroup = this.form.get('address');
      if (addressGroup) {
        addressGroup.patchValue({
          city: place,
          street: a.street ?? '',
          streetNumber: a.houseNumber ?? '',
          postalCode: a.postalCode ?? '',
          country: a.country ?? 'Polska',
          // Z mapy nie wymuszamy "Brak ulicy" — ulica zostaje edytowalna.
          noStreet: false,
        });
      }

      // Zapamiętaj query po kliku w mapę (z aktualnych pól formularza), żeby kolejny
      // (blur) na numerze nie wystrzelił tego samego adresu drugi raz.
      this.lastGeocodedQuery = this.buildAddressGeocodeQuery();
    }

    this.form.markAsDirty();
    this.saveToState();
  }

  // ==================== AUTOCOMPLETE / GEOCODING ====================

  /**
   * Ustawia początkowy stan readonly pól ulicy/numeru i nasłuchuje zmian
   * miejscowości / "Brak ulicy", przeliczając go (patrz syncAddressFieldState()).
   */
  private setupAddressFieldEnableLogic(): void {
    const addr = this.form.get('address') as FormGroup;
    if (!addr) return;

    // Stan początkowy
    this.syncAddressFieldState();

    addr.get('city')?.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.syncAddressFieldState());
    addr.get('street')?.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.syncAddressFieldState());
    addr.get('noStreet')?.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.syncAddressFieldState());
  }

  /**
   * Przelicza stan "tylko do odczytu" pól ulicy i numeru w zależności od adresu:
   * - puste miasto → ulica + numer tylko do odczytu (najpierw wybierz miejscowość)
   * - zaznaczone "Brak ulicy" → ulica tylko do odczytu (wieś bez ulic)
   * - numer pozostaje dostępny przy wypełnionej miejscowości — także dla wsi
   *   bez ulic (adres typu "Paprotno 12")
   *
   * Kontrolki ZOSTAJĄ enabled — blokadę pokazujemy wyłącznie wizualnie przez
   * [readonly] w szablonie. Dzięki temu:
   *  - input nigdy nie połyka klawiszy (wyłączony p-autoComplete byłby `disabled`),
   *  - odznaczenie "Brak ulicy" natychmiast odblokowuje ulicę,
   *  - zewnętrzna biblioteka mapy nadal może zapisywać do tych pól (reverse-geocode).
   *
   * Wywoływane przy zmianach pól ORAZ ręcznie po patchu formularza ze stanu —
   * patch używa { emitEvent: false }, więc nie wyzwala subskrypcji valueChanges.
   */
  private syncAddressFieldState(): void {
    const addr = this.form.get('address') as FormGroup;
    if (!addr) return;

    const city = String(addr.get('city')?.value ?? '').trim();
    const noStreet = addr.get('noStreet')?.value === true;

    this.streetReadonly.set(city.length === 0 || noStreet);
    this.streetNumberReadonly.set(city.length === 0);

    // Box ulic obowiązuje tylko dla miasta, dla którego został pobrany
    // (onCitySelect). Gdy miejscowość jest pusta LUB zmieniła się na inną —
    // ręcznie wpisaną albo podstawioną z mapy (reverse-geocode) — poprzedni box
    // jest nieaktualny i mógłby ukrywać poprawne ulice nowego miasta, więc go
    // czyścimy: wyszukiwanie ulicy znów jest nieograniczone, dopóki użytkownik
    // nie wybierze nowej miejscowości z podpowiedzi.
    if (city.length === 0 || city !== this.boundsCity) {
      this.selectedCityBounds = null;
      this.boundsCity = null;
    }
  }

  onCitySearch(event: AutoCompleteCompleteEvent): void {
    this.citySearch$.next(event.query);
  }

  onCitySelect(event: AutoCompleteSelectEvent): void {
    const suggestion = event.value as AutocompleteSuggestion;
    const a = suggestion.address ?? {};
    const place = a.hamlet ?? a.village ?? a.town ?? a.city ?? '';

    // Zapamiętaj granice miasta — wyszukiwanie ulic ograniczymy do tego boxa.
    // boundsCity ustawiamy PRZED patchem, żeby wywołane nim syncAddressFieldState()
    // rozpoznało, że box należy do właśnie wybranej miejscowości i go nie skasowało.
    this.selectedCityBounds = suggestion.boundingBox ?? null;
    this.boundsCity = place;

    // Zmiana miasta = nowy adres: czyścimy ulicę i numer, ale ulica ZOSTAJE
    // edytowalna (noStreet=false). Wcześniejsze auto-wykrywanie "wsi bez ulic"
    // było zawodne (większość miejscowości OSM to village/hamlet) i blokowało
    // wpisanie ulicy po zmianie miejscowości — "Brak ulicy" zaznacza teraz
    // wyłącznie użytkownik ręcznie.
    this.form.get('address')?.patchValue({
      city: place,
      street: '',
      streetNumber: '',
      postalCode: a.postalCode ?? '',
      country: a.country ?? 'Polska',
      noStreet: false
    });

    this.applyLocationFromSuggestion(suggestion);
  }

  onStreetSearch(event: AutoCompleteCompleteEvent): void {
    // p-autoComplete z formControlName może chwilowo trzymać AutocompleteSuggestion
    // obiekt (między select a naszym patchValue) — normalizujemy defensywnie do stringa.
    const cityRaw = this.form.get('address.city')?.value;
    const city = typeof cityRaw === 'string'
      ? cityRaw
      : ((cityRaw as AutocompleteSuggestion | null)?.address?.city
          ?? (cityRaw as AutocompleteSuggestion | null)?.formatted
          ?? '');
    const query = city.trim().length >= 2
      ? `${event.query}, ${city}`
      : event.query;
    this.streetSearch$.next(query);
  }

  onStreetSelect(event: AutoCompleteSelectEvent): void {
    const suggestion = event.value as AutocompleteSuggestion;
    const a = suggestion.address ?? {};
    const place = a.hamlet ?? a.village ?? a.town ?? a.city ?? '';
    const currentCity = this.form.get('address.city')?.value;
    const currentCityStr = typeof currentCity === 'string' ? currentCity.trim() : '';

    this.form.get('address')?.patchValue({
      // NIE nadpisujemy miejscowości wybranej przez użytkownika. Geokoder często
      // zwraca ulicę z sąsiedniej miejscowości OSM (np. "Lśniących Wód" → Dębina),
      // choć użytkownik ustawił np. Ustkę — zachowujemy jego wybór; `place`
      // używamy tylko, gdy pole miejscowości jest jeszcze puste.
      city: currentCityStr || place,
      street: a.street ?? '',
      streetNumber: a.houseNumber ?? '',
      postalCode: a.postalCode ?? this.form.get('address.postalCode')?.value ?? '',
      country: a.country ?? 'Polska',
      // Wybór z pola ulicy → ulica pozostaje edytowalna; "Brak ulicy" tylko ręcznie.
      noStreet: false
    });

    this.applyLocationFromSuggestion(suggestion);
  }

  /**
   * Buduje znormalizowane zapytanie geokodera z AKTUALNYCH pól adresu formularza.
   * Używane spójnie do ZAPISU `lastGeocodedQuery` (po wyborze miasta/ulicy oraz po
   * kliknięciu w mapę) ORAZ do porównania w onStreetNumberBlur(). Dzięki temu klucz
   * deduplikacji jest identyczny niezależnie od tego, czy miejscowość pochodzi z
   * wyboru użytkownika, czy z podpowiedzi geokodera (która może wskazywać sąsiednią).
   */
  private buildAddressGeocodeQuery(): string {
    const a = this.form.get('address')?.getRawValue() ?? {};
    return [
      `${a.street ?? ''} ${a.streetNumber ?? ''}`.trim(),
      a.city,
      a.country ?? 'Polska'
    ].filter(p => p && String(p).trim()).join(', ');
  }

  /**
   * Po wyjściu z pola numeru — geokodujemy pełen adres i przesuwamy marker.
   * Bezpieczne, bo numer to mała korekta w obrębie wybranej już ulicy.
   */
  onStreetNumberBlur(): void {
    const a = this.form.get('address')?.getRawValue();
    if (!a?.city || !a?.street) return;

    const query = this.buildAddressGeocodeQuery();

    if (query === this.lastGeocodedQuery) return;
    this.lastGeocodedQuery = query;

    this.geocoding
      .forward(query, { ...this.geocodingConfig, maxSuggestions: 1 })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(locs => {
        if (!locs[0]?.coordinates) return;
        const coords = locs[0].coordinates;
        this.form.patchValue({
          location: { latitude: coords.lat, longitude: coords.lng }
        });
        this.setMapMarker(coords);

        // Uzupełnij kod pocztowy z wyniku, jeśli pole jest jeszcze puste
        const postalCode = locs[0].address?.postalCode;
        const currentPostal = this.form.get('address.postalCode')?.value;
        if (postalCode && !(currentPostal && String(currentPostal).trim())) {
          this.form.get('address')?.patchValue({ postalCode });
        }

        this.form.markAsDirty();
      });
  }

  onNoStreetToggle(checked: boolean): void {
    if (checked) {
      // Czyścimy tylko ulicę — numer zostaje, bo wsie bez ulic mają adres "miejscowość + numer"
      this.form.get('address')?.patchValue({ street: '' });
    }
    this.form.markAsDirty();
  }

  private applyLocationFromSuggestion(suggestion: AutocompleteSuggestion): void {
    if (suggestion.coordinates) {
      const coords = suggestion.coordinates;
      this.form.patchValue({
        location: { latitude: coords.lat, longitude: coords.lng }
      });
      this.setMapMarker(coords);

      // Zapisz query z RZECZYWISTYCH pól formularza (już po patchu miasta/ulicy),
      // a nie z `place` podpowiedzi — bo onStreetSelect zachowuje miejscowość wybraną
      // przez użytkownika, podczas gdy geokoder może zwrócić sąsiednią. Inaczej klucz
      // nie pasowałby do tego z onStreetNumberBlur() i (blur) strzelałby tym samym
      // adresem mimo braku zmiany.
      this.lastGeocodedQuery = this.buildAddressGeocodeQuery();

      // Podpowiedzi z wyszukiwarki często nie mają kodu pocztowego (zwłaszcza wsie) —
      // dociągamy go reverse geokodowaniem po współrzędnych.
      const a = suggestion.address ?? {};
      if (!a.postalCode) {
        this.fillPostalCodeFromCoords(coords);
      }
    }
    this.form.markAsDirty();
    this.saveToState();
  }

  /** Uzupełnia kod pocztowy reverse geokodowaniem — tylko gdy pole jest puste. */
  private fillPostalCodeFromCoords(coords: Coordinates): void {
    this.geocoding
      .reverse(coords, this.geocodingConfig)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(location => {
        const postalCode = location.address?.postalCode;
        if (!postalCode) return;

        const current = this.form.get('address.postalCode')?.value;
        if (current && String(current).trim()) return;

        this.form.get('address')?.patchValue({ postalCode });
        this.form.markAsDirty();
        this.saveToState();
      });
  }

  /** Pierwsza linia podpowiedzi — adres uliczny albo nazwa miejscowości (hamlet > village > ...). */
  getSuggestionPrimary(item: AutocompleteSuggestion): string {
    const a = item.address;
    if (a?.street) {
      return a.houseNumber ? `${a.street} ${a.houseNumber}` : a.street;
    }
    return a?.hamlet ?? a?.village ?? a?.suburb ?? a?.neighbourhood ?? a?.town ?? a?.city
      ?? item.formatted.split(',')[0]?.trim() ?? item.formatted;
  }

  /** Druga linia — kontekst administracyjny (kod, gmina, powiat, województwo, kraj). */
  getSuggestionSecondary(item: AutocompleteSuggestion): string {
    const a = item.address ?? {};
    const parts: string[] = [];
    if (a.street) {
      const place = a.hamlet ?? a.village ?? a.town ?? a.city;
      if (place) parts.push(a.postalCode ? `${a.postalCode} ${place}` : place);
    } else if (a.hamlet) {
      // Przysiółek — pokaż macierzystą wieś/miasto żeby user odróżnił sąsiednie hamlety o tej samej nazwie
      const parent = a.village ?? a.town ?? a.city;
      if (parent) parts.push(a.postalCode ? `${a.postalCode} ${parent}` : parent);
      else if (a.postalCode) parts.push(a.postalCode);
    }
    if (a.municipality && !parts.includes(a.municipality)) parts.push(a.municipality);
    if (a.county && !parts.includes(a.county)) parts.push(a.county);
    if (a.state && !parts.includes(a.state)) parts.push(a.state);
    if (a.country && !parts.includes(a.country)) parts.push(a.country);
    return parts.join(' · ');
  }

  /** Plakietka z typem miejsca (wieś / miasto / przysiółek / ulica / ...). */
  getSuggestionBadge(item: AutocompleteSuggestion): string | null {
    return item.type ? this.placeLabels[item.type] ?? null : null;
  }

  getErrorMessage(controlPath: string): string | null {
    const control = this.form.get(controlPath);
    if (!control || !control.errors || (!control.touched && !control.dirty && !this.submitted())) return null;

    const errors = control.errors;
    if (errors['required']) return this.translocoService.translate('objects.wizard.validation.required');
    if (errors['minlength']) return this.translocoService.translate('objects.wizard.validation.minlength', { length: errors['minlength'].requiredLength });
    if (errors['maxlength']) return this.translocoService.translate('objects.wizard.validation.maxlength', { length: errors['maxlength'].requiredLength });
    if (errors['min']) return this.translocoService.translate('objects.wizard.validation.minValue', { min: errors['min'].min });
    if (errors['max']) return this.translocoService.translate('objects.wizard.validation.maxValue', { max: errors['max'].max });
    if (errors['pattern']) {
      if (controlPath === 'address.postalCode') {
        return this.translocoService.translate('objects.wizard.validation.postalCodePattern');
      }
      return this.translocoService.translate('objects.wizard.validation.invalidPhone');
    }
    if (errors['email']) return this.translocoService.translate('objects.wizard.validation.invalidEmail');

    return this.translocoService.translate('objects.wizard.validation.invalidValue');
  }

  onCancel(): void {
    this.cancelled.emit();
  }

  onNext(): void {
    this.submitted.set(true);
    this.markAllAsTouched();
    this.saveToState();
    this.updateMissingFields();

    if (this.canGoNext()) {
      this.stateService.nextStep();
    }
  }

  private markAllAsTouched(): void {
    Object.keys(this.form.controls).forEach(key => {
      const control = this.form.get(key);
      control?.markAsTouched();
      control?.markAsDirty();
      if (control instanceof FormGroup) {
        Object.keys(control.controls).forEach(childKey => {
          control.get(childKey)?.markAsTouched();
          control.get(childKey)?.markAsDirty();
        });
      }
    });
  }

  private updateMissingFields(): void {
    const t = (key: string) => this.translocoService.translate(key);
    const missing: string[] = [];

    const images = this.form.get('images')?.value;
    if (!images || images.length < this.limits.PROPERTY_MIN_IMAGES) {
      missing.push(t('objects.wizard.basicInfo.gallery'));
    } else if (this.galleryFailedCount() > 0) {
      missing.push(
        this.translocoService.translate('objects.wizard.gallery.uploadFailed', {
          count: this.galleryFailedCount(),
        }),
      );
    } else if (this.galleryPendingCount() > 0) {
      missing.push(
        this.translocoService.translate('objects.wizard.gallery.uploadPending', {
          count: this.galleryPendingCount(),
        }),
      );
    }

    if (this.form.get('name')?.invalid) {
      missing.push(t('objects.wizard.basicInfo.name'));
    }

    if (this.form.get('description')?.invalid) {
      missing.push(t('objects.wizard.basicInfo.description'));
    }

    const addressGroup = this.form.get('address') as FormGroup;
    if (addressGroup?.invalid) {
      missing.push(t('objects.wizard.basicInfo.address'));
    }

    if (this.form.get('phone')?.invalid) {
      missing.push(t('objects.wizard.contactVisibility.showPhone'));
    }

    if (this.form.get('whatsApp')?.invalid) {
      missing.push('WhatsApp');
    }

    if (this.form.get('email')?.invalid) {
      missing.push('Email');
    }

    this.missingFields.set(missing);
  }
}
